import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { map } from 'rxjs/operators';

import {
  FavoriteMilitaryOccupation,
  GetOccupationDetailGQL,
  GetSimilarMilitaryOccupationsGQL,
  OccupationForExportPdfGQL
} from '@cpd-app/generated';

import { OccupationDetail, OccupationForExportPdf } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class OccupationService {
  constructor(
    private getOccupationDetailGQL: GetOccupationDetailGQL,
    private occupationForExportPdfGQL: OccupationForExportPdfGQL,
    private getSimilarMilitaryOccupationsGQL: GetSimilarMilitaryOccupationsGQL
  ) {}

  public getOccupationDetail(
    onetCode: string,
    jobId: string,
    jobTitle?: string
  ): Observable<OccupationDetail> {
    return this.getOccupationDetailGQL
      .fetch({
        onetCode,
        jobId,
        jobTitle
      })
      .pipe(map((results) => results?.data?.occupation as any));
  }

  /**
   * The detail AND whether the API reported errors (code review, 30 Sep).
   *
   * Queries run with errorPolicy 'all', so a server error arrives as data with
   * `occupation: null` and an `errors` list, which getOccupationDetail above
   * cannot tell apart from a code that has no occupation. Only a clean answer
   * with no occupation is "not found".
   */
  public getOccupationDetailResult(
    onetCode: string,
    jobId: string,
    jobTitle?: string
  ): Observable<{ occupation: OccupationDetail | null; hasErrors: boolean }> {
    return this.getOccupationDetailGQL
      .fetch({
        onetCode,
        jobId,
        jobTitle
      })
      .pipe(
        map((results) => ({
          // The same cast getOccupationDetail makes: the generated type differs.
          occupation: (results?.data?.occupation as unknown as OccupationDetail) ?? null,
          hasErrors: !!results?.errors?.length || !!results?.error
        }))
      );
  }

  public getSimilarMilitaryOccupations(
    onetCode: string,
    search: string
  ): Observable<FavoriteMilitaryOccupation[]> {
    return this.getSimilarMilitaryOccupationsGQL
      .fetch({ onetCode, search })
      .pipe(map((result) => result.data.getSimilarMilitaryOccupations));
  }
  public getMilitaryOccupationsForGoal(
    search: string
  ): Observable<FavoriteMilitaryOccupation[]> {
    return this.getSimilarMilitaryOccupationsGQL
      .fetch({ search })
      .pipe(map((result) => result.data.getSimilarMilitaryOccupations));
  }

  public getOccupationForExportPdf(
    onetCode: string,
    jobId: string,
    jobTitle?: string
  ): Observable<OccupationForExportPdf> {
    return this.occupationForExportPdfGQL
      .fetch({
        onetCode,
        jobId,
        jobTitle
      })
      .pipe(map((result) => result.data.occupation as OccupationForExportPdf));
  }
}
